import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "../api/api-error";

type Settled<Data> = { status: "ready"; data: Data } | { status: "failed"; error: ApiError };
export type Resource<Data> = { status: "loading" } | Settled<Data>;

/**
 * Loads something from the API for as long as the component is mounted. Each request has
 * its own identity, and only the answer to the current request is ever shown: one made
 * obsolete by a newer request, or by unmounting, is aborted and ignored.
 */
export function useResource<Data>(
  load: (signal: AbortSignal) => Promise<Data>,
): Resource<Data> & { retry: () => void } {
  const [attempt, setAttempt] = useState(0);
  const [answer, setAnswer] = useState<{ to: object; result: Settled<Data> } | null>(null);
  const current = useMemo(() => ({ load, attempt }), [load, attempt]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal).then(
      (data) => setAnswer({ to: current, result: { status: "ready", data } }),
      (error: unknown) => {
        if (controller.signal.aborted) return;
        const failure =
          error instanceof ApiError
            ? error
            : new ApiError("unexpected_response", "Something went wrong loading this.");
        setAnswer({ to: current, result: { status: "failed", error: failure } });
      },
    );
    return () => controller.abort();
  }, [load, current]);

  const retry = useCallback(() => setAttempt((count) => count + 1), []);
  const resource: Resource<Data> = answer?.to === current ? answer.result : { status: "loading" };
  return { ...resource, retry };
}
